'use client';

import Link from 'next/link';
import { useMemo } from 'react';
import { FormField } from '~/components/core/FormField';
import Button from '~/components/customComponents/buttons/Button';
import Flex from '~/components/customComponents/flex/Flex';
import Text from '~/components/customComponents/texts/Text';
import { useI18n } from '~/locales/client';

/**
 * Renders a form for resetting a password. The form includes an email input field
 * and a reset button. The form is disabled if `isCTADisabled` is true and the
 * reset button is loading if `isSendButtonLoading` is true.The `handleFormResetSubmit` function is called when the form is
 * submitted. The `onEmailChange` function is called when the email input field's
 * value changes.
 *
 * @param {Object} props - The component's props.
 * @param {boolean} props.isCTADisabled - Whether the form is disabled.
 * @param {(e: React.FormEvent<HTMLFormElement>) => void} props.handleFormResetSubmit - The function to call when the form is submitted.
 * @param {(value: string) => void} props.onEmailChange - The function to call when the email input field's value changes.
 * @param {string} props.email - The current value of the email input field.
 * @param {boolean} props.isSendButtonLoading - Whether the reset button is loading.
 * @return {JSX.Element} The rendered form component.
 */
const ResetPassword = ({
  isCTADisabled,
  handleFormResetSubmit,
  onEmailChange,
  email,
  isSendButtonLoading,
}: {
  isCTADisabled: boolean;
  handleFormResetSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  onEmailChange: (value: string) => void;
  email: string;
  isSendButtonLoading: boolean;
}) => {
  const translate = useI18n();

  const copy = useMemo(
    () => ({
      titleLabel: translate('page.forgot-password.view.title'),
      descriptionLabel: translate('page.forgot-password.view.description'),
      emailLabel: translate('page.forgot-password.view.email.label'),
      emailPlaceholder: translate('page.forgot-password.view.email.placeholder'),
      backButtonLabel: translate('page.forgot-password.view.button.back.label'),
      backButtonHref: translate('page.forgot-password.view.button.back.href'),
      resetButtonLabel: translate('page.forgot-password.view.button.reset.label'),
    }),
    [translate],
  );

  return (
    <>
      <Flex className="flex-col mb-8">
        <Flex className="flex-col">
          <Text variant="h1">{copy.titleLabel}</Text>
          <Text color="gray" className="mt-2.5">
            {copy.descriptionLabel}
          </Text>
        </Flex>
      </Flex>
      <form onSubmit={handleFormResetSubmit}>
        <FormField
          label={copy.emailLabel}
          type="email"
          placeholder={copy.emailPlaceholder}
          onChange={onEmailChange}
          value={email}
        />
        <Button
          buttonClassName="hover:bg-red-700"
          disabled={isCTADisabled}
          isLoading={isSendButtonLoading}
          type="submit"
          text={copy.resetButtonLabel}
        />
      </form>
      <Flex className="justify-center mt-2">
        <Link href={copy.backButtonHref} className="text-base hover:underline">
          {copy.backButtonLabel}
        </Link>
      </Flex>
    </>
  );
};

export default ResetPassword;
